/**
 * ================================================================
 * CUSTOM HOOK: useFinancialYear
 * ================================================================
 *
 * @fileOverview     Custom hook for financial year state management
 * @module           hooks/useFinancialYear
 * @version          1.0.0
 * @since            2025-07-30
 * @author           Development Team <dev@company.com>
 * @lastModified     2025-07-30
 *
 * @purpose          Provides clean interface for financial year state and actions
 * @dependencies     React hooks, Redux store, financial year slice
 * @exports          useFinancialYear hook with state and utility functions
 *
 * @architecture
 * - Custom hook pattern
 * - Redux state integration
 * - Utility function composition
 *
 * @performance
 * - Memoized selectors for performance
 * - Efficient state updates
 * - Minimal re-renders
 *
 * @security         No security concerns in financial year selection
 * @accessibility    Financial year changes announced to screen readers
 * @testing          Unit tests for all hook functionality
 *
 * @relatedFiles
 * - /src/store/slices/financialYearSlice.ts - Redux slice
 * - /src/lib/utils/dateUtils.ts - Date utility functions
 *
 * ================================================================
 */

import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  initializeFinancialYear,
  resetFinancialYear,
  selectAvailableFinancialYears,
  selectFinancialYear,
  selectSelectedFinancialYear,
  setFinancialYear,
} from '@/store/slices/financialYearSlice';

// ================================================================
// HOOK INTERFACE
// ================================================================

/**
 * Return type for useFinancialYear hook
 *
 * @interface UseFinancialYearReturn
 * @property {string} selectedFinancialYear - Display format: "2024-2025"
 * @property {string} financialYear - API format: "2024"
 * @property {string[]} availableFinancialYears - All available years
 * @property {Function} changeFinancialYear - Update financial year
 * @property {Function} resetToDefaultYear - Reset to current FY
 * @property {Function} initializeFromStorage - Initialize from localStorage
 * @property {Function} getCurrentFinancialYearForAPI - Get API format
 * @property {Function} getCurrentFinancialYearDisplay - Get display format
 * @property {Function} isYearSelected - Check if specific year is selected
 */
export interface UseFinancialYearReturn {
  /** Current selected financial year for UI display (format: "2024-2025") */
  selectedFinancialYear: string;
  /** Financial year for API calls (format: "2024") */
  financialYear: string;
  /** All available financial years for selection */
  availableFinancialYears: string[];
  /** Function to change the selected financial year */
  changeFinancialYear: (year: string) => void;
  /** Function to reset to default financial year */
  resetToDefaultYear: () => void;
  /** Function to initialize from localStorage */
  initializeFromStorage: () => void;
  /** Get current financial year in API format */
  getCurrentFinancialYearForAPI: () => string;
  /** Get current financial year in display format */
  getCurrentFinancialYearDisplay: () => string;
  /** Check if a specific year is currently selected */
  isYearSelected: (year: string) => boolean;
}

// ================================================================
// MAIN HOOK IMPLEMENTATION
// ================================================================

/**
 * Custom hook for financial year state management
 *
 * @hook useFinancialYear
 * @description Provides centralized access to financial year state and actions
 * @category Business Logic Hook
 *
 * @returns {UseFinancialYearReturn} Financial year state and utility functions
 *
 * @sideEffects
 * - Dispatches Redux actions when financial year changes
 * - Updates localStorage for persistence
 * - May trigger API refetches in consuming components
 *
 * @dependencies
 * - useAppSelector: Access Redux state
 * - useAppDispatch: Dispatch Redux actions
 * - Financial year slice actions and selectors
 *
 * @usage
 * ```tsx
 * const {
 *   selectedFinancialYear,
 *   financialYear,
 *   availableFinancialYears,
 *   changeFinancialYear,
 *   resetToDefaultYear,
 *   isYearSelected
 * } = useFinancialYear();
 *
 * // In component render
 * <select value={selectedFinancialYear} onChange={(e) => changeFinancialYear(e.target.value)}>
 *   {availableFinancialYears.map(year => (
 *     <option key={year} value={year}>{year}</option>
 *   ))}
 * </select>
 *
 * // In API calls
 * const { data } = useGetReportQuery({ financialYear, ...otherParams });
 * ```
 *
 * @since 1.0.0
 * @see {@link setFinancialYear} for Redux action
 */
export function useFinancialYear(): UseFinancialYearReturn {
  // ================================================================
  // REDUX STATE & DISPATCH
  // ================================================================

  const dispatch = useAppDispatch();

  // Use individual selectors for better performance
  const selectedFinancialYear = useAppSelector(selectSelectedFinancialYear);
  const financialYear = useAppSelector(selectFinancialYear);
  const availableFinancialYears = useAppSelector(selectAvailableFinancialYears);

  // ================================================================
  // ACTION CREATORS
  // ================================================================

  /**
   * Change the selected financial year
   * Triggers Redux action to update state and localStorage
   *
   * @function changeFinancialYear
   * @param {string} year - Financial year in "YYYY-YYYY" format
   *
   * @example
   * ```typescript
   * changeFinancialYear('2023-2024');
   * // Updates Redux state and triggers dependent components to refresh
   * ```
   */
  const changeFinancialYear = useCallback(
    (year: string) => {
      if (!year || !availableFinancialYears.includes(year)) {
        console.warn(`Invalid financial year: ${year}`);
        return;
      }

      console.log('🔄 useFinancialYear: changeFinancialYear called', {
        currentYear: selectedFinancialYear,
        newYear: year,
        timestamp: new Date().toISOString()
      });

      dispatch(setFinancialYear(year));

      // Optional: Announce change for accessibility
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        try {
          const announcement = `Financial year changed to ${year}`;
          const utterance = new SpeechSynthesisUtterance(announcement);
          utterance.volume = 0; // Silent announcement for screen readers
          window.speechSynthesis.speak(utterance);
        } catch (error) {
          // Fail silently for accessibility enhancement
        }
      }
    },
    [dispatch, availableFinancialYears],
  );

  /**
   * Reset financial year to default (current FY)
   *
   * @function resetToDefaultYear
   * @example
   * ```typescript
   * resetToDefaultYear();
   * // Resets to current financial year and clears localStorage
   * ```
   */
  const resetToDefaultYear = useCallback(() => {
    dispatch(resetFinancialYear());
  }, [dispatch]);

  /**
   * Initialize financial year from localStorage
   * Called during app initialization
   *
   * @function initializeFromStorage
   * @example
   * ```typescript
   * initializeFromStorage();
   * // Loads persisted year or defaults to current FY
   * ```
   */
  const initializeFromStorage = useCallback(() => {
    dispatch(initializeFinancialYear());
  }, [dispatch]);

  // ================================================================
  // UTILITY FUNCTIONS
  // ================================================================

  /**
   * Get current financial year in API format
   *
   * @function getCurrentFinancialYearForAPI
   * @returns {string} Financial year in "YYYY" format
   * @example
   * ```typescript
   * const apiYear = getCurrentFinancialYearForAPI(); // "2024"
   * ```
   */
  const getCurrentFinancialYearForAPI = useCallback((): string => {
    return financialYear;
  }, [financialYear]);

  /**
   * Get current financial year in display format
   *
   * @function getCurrentFinancialYearDisplay
   * @returns {string} Financial year in "YYYY-YYYY" format
   * @example
   * ```typescript
   * const displayYear = getCurrentFinancialYearDisplay(); // "2024-2025"
   * ```
   */
  const getCurrentFinancialYearDisplay = useCallback((): string => {
    return selectedFinancialYear;
  }, [selectedFinancialYear]);

  /**
   * Check if a specific year is currently selected
   *
   * @function isYearSelected
   * @param {string} year - Year to check in "YYYY-YYYY" format
   * @returns {boolean} True if the year is currently selected
   * @example
   * ```typescript
   * const isCurrentYear = isYearSelected('2024-2025'); // true/false
   * ```
   */
  const isYearSelected = useCallback(
    (year: string): boolean => {
      return selectedFinancialYear === year;
    },
    [selectedFinancialYear],
  );

  // ================================================================
  // MEMOIZED RETURN VALUE
  // ================================================================

  /**
   * Memoized return object to prevent unnecessary re-renders
   * Only updates when financial year state changes
   */
  const returnValue = useMemo(
    () => ({
      selectedFinancialYear,
      financialYear,
      availableFinancialYears,
      changeFinancialYear,
      resetToDefaultYear,
      initializeFromStorage,
      getCurrentFinancialYearForAPI,
      getCurrentFinancialYearDisplay,
      isYearSelected,
    }),
    [
      selectedFinancialYear,
      financialYear,
      availableFinancialYears,
      changeFinancialYear,
      resetToDefaultYear,
      initializeFromStorage,
      getCurrentFinancialYearForAPI,
      getCurrentFinancialYearDisplay,
      isYearSelected,
    ],
  );

  return returnValue;
}

/**
 * ================================================================
 * END OF HOOK: useFinancialYear
 * ================================================================
 *
 * @summary          Custom hook for centralized financial year management
 * @exports          useFinancialYear hook with state and utility functions
 * @dependencies     React hooks, Redux store, financial year slice
 * @usedBy           Header component, report pages, financial forms
 *
 * @maintainers
 * - Primary: Development Team <dev@company.com>
 *
 * @changelog
 * - 2025-07-30: Initial implementation with Redux integration
 *
 * @todos
 * - [ ] Add financial year validation hooks
 * - [ ] Implement financial year change callbacks
 * - [ ] Add financial year analytics tracking
 *
 * @references
 * - Documentation: Financial Year Developer Guide
 * - Pattern: Custom hooks with Redux
 *
 * ================================================================
 */
